import { Injectable, NgZone } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { UserService } from 'src/app/auth/service/user/user.service';
import { environment } from 'src/environments/environment';
import { PlaybookRef } from '../models/ai-agents.models';
import { aiAgentsSystemBaseUrl } from './ai-agents-api.service';
import { readSseStream, SseEvent } from './sse-reader';

export interface TestChatDone {
  messageId?: string;
  conversationId?: string;
  inputTokens?: number;
  outputTokens?: number;
}

export interface TestChatHandlers {
  onDelta: (text: string) => void;
  onDone?: (done: TestChatDone) => void;
  onError?: (message: string) => void;
  /** Called as soon as the response headers arrive, with X-Conversation-Id. */
  onConversationId?: (conversationId: string) => void;
  /** Contract v1.4: `event: playbook` {key, title}, once per playbook the agent read, before the answer. */
  onPlaybook?: (playbook: PlaybookRef) => void;
}

export interface TestChatResult {
  conversationId: string | null;
  completed: boolean;
}

/** Fetch-compatible function, injectable for tests. */
export type FetchFn = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * Streams POST agents/{id}/test-chat (text/event-stream) with fetch + ReadableStream.
 * HttpClient cannot stream a POST body as it arrives, so the token store is read directly;
 * a 401 refreshes the access token once (same flow as TokenInterceptor) and retries.
 */
@Injectable({ providedIn: 'root' })
export class TestChatClientService {
  fetchFn: FetchFn = (input, init) => fetch(input, init);

  constructor(private user: UserService, private zone: NgZone) {}

  async stream(
    agentId: string,
    text: string,
    conversationId: string | null,
    handlers: TestChatHandlers,
    signal?: AbortSignal
  ): Promise<TestChatResult> {
    const url = `${aiAgentsSystemBaseUrl()}/agents/${encodeURIComponent(agentId)}/test-chat`;
    const body = JSON.stringify(conversationId ? { text, conversationId } : { text });

    let response = await this.post(url, body, signal);
    if (response.status === 401) {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        response = await this.post(url, body, signal);
      }
    }

    if (!response.ok || !response.body) {
      const message = await errorMessage(response);
      this.run(() => handlers.onError?.(message));
      return { conversationId, completed: false };
    }

    let currentId = response.headers.get('X-Conversation-Id') || conversationId;
    if (currentId && currentId !== conversationId) {
      const id = currentId;
      this.run(() => handlers.onConversationId?.(id));
    }

    let completed = false;
    await readSseStream(response.body, (ev: SseEvent) => {
      const data = parseJson(ev.data);
      if (ev.event === 'delta') {
        const piece = typeof data?.text === 'string' ? data.text : '';
        if (piece) {
          this.run(() => handlers.onDelta(piece));
        }
      } else if (ev.event === 'done') {
        completed = true;
        const done: TestChatDone = data || {};
        if (done.conversationId) {
          currentId = done.conversationId;
        }
        this.run(() => handlers.onDone?.(done));
      } else if (ev.event === 'error') {
        const message = typeof data?.message === 'string' ? data.message : ev.data || 'The agent could not answer.';
        this.run(() => handlers.onError?.(message));
      } else if (ev.event === 'playbook') {
        const key = typeof data?.key === 'string' ? data.key : '';
        const title = typeof data?.title === 'string' && data.title ? data.title : key;
        if (title) {
          this.run(() => handlers.onPlaybook?.({ key, title }));
        }
      }
      // Any other event name is ignored (contract v1.4: clients ignore SSE events they do not know).
    });
    return { conversationId: currentId, completed };
  }

  private post(url: string, body: string, signal?: AbortSignal): Promise<Response> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    };
    const token = this.user.getAcessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (environment.XprojectId) {
      headers['X-Project-Id'] = environment.XprojectId;
    }
    return this.fetchFn(url, { method: 'POST', headers, body, signal, cache: 'no-store' });
  }

  private async refreshToken(): Promise<boolean> {
    try {
      const res: any = await firstValueFrom(this.user.refreshToken());
      if (res?.accessToken) {
        this.user.setAccessToken(res.accessToken);
        return true;
      }
    } catch {
      // fall through: the caller reports the 401
    }
    return false;
  }

  private run(fn: () => void): void {
    this.zone.run(fn);
  }
}

function parseJson(raw: string): any {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

async function errorMessage(response: Response): Promise<string> {
  let detail = '';
  try {
    const text = await response.text();
    const json = parseJson(text);
    detail = json?.message || json?.error || text;
  } catch {
    detail = '';
  }
  const prefix = `HTTP ${response.status}`;
  return detail ? `${prefix}: ${String(detail).slice(0, 300)}` : prefix;
}
